import * as React from "react"
import { usePage } from "@inertiajs/react"
import { CreditCard, KeyRound, LifeBuoy, Receipt, Users } from "lucide-react"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"
import { MainNav, type NavItemDef } from "@/components/MainNav"
import type { PageProps } from "@/types/inertia"

// Shell for the logged-in account area. Everyone lands on their own License
// page; owners additionally manage seats/billing/payments. Support appears
// only for accounts holding a valid license.
export function AccountShell({ children }: { children: React.ReactNode }) {
  const { props } = usePage<PageProps>()
  const account = props.current_account

  const items: NavItemDef[] = [
    {
      href: "/account/license",
      icon: KeyRound,
      label: "License",
      match: (url) => url === "/" || url.startsWith("/account/license"),
    },
  ]

  if (account?.role === "owner") {
    items.push(
      {
        href: "/account/seats",
        icon: Users,
        label: "Seats & Team",
        match: (url) => url.startsWith("/account/seats"),
      },
      {
        href: "/account/billing",
        icon: CreditCard,
        label: "Billing",
        match: (url) => url.startsWith("/account/billing"),
      },
      {
        href: "/account/payments",
        icon: Receipt,
        label: "Payments",
        match: (url) => url.startsWith("/account/payments"),
      },
    )
  }

  if (account?.supportable) {
    items.push({
      href: "/account/support",
      icon: LifeBuoy,
      label: "Support",
      match: (url) => url.startsWith("/account/support"),
    })
  }

  return (
    <div className="flex min-h-screen flex-col bg-page text-ink-body">
      <ImpersonationBanner />
      <div className="flex min-h-0 flex-1">
        <MainNav items={items} brandHref="/account/license" />
        <main className="min-w-0 flex-1 px-6 py-8 sm:px-10">
          <div className="mx-auto max-w-4xl">
            {(props.flash?.notice || props.flash?.alert) && (
              <div className="mb-6 space-y-2">
                {props.flash.notice && (
                  <div className="callout callout-accent">
                    <p className="text-sm">{props.flash.notice}</p>
                  </div>
                )}
                {props.flash.alert && (
                  <div className="callout callout-danger">
                    <p className="text-sm">{props.flash.alert}</p>
                  </div>
                )}
              </div>
            )}
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}
